import React, { useEffect, useRef, useState } from "react";

import { cn } from "../../../common/u1/cn";
import {
  ChevronDown,
  DocumentIcon,
  UsersIcon,
  UploadIcon,
  StatusGlyph,
} from "../../../../assets/ui/icons";
import { ReactComponent as Linkedin } from "../../../../assets/icons/socialIcons/linkedinIcon.svg";
import { ReactComponent as Twitter } from "../../../../assets/icons/socialIcons/TWITTER.svg";
import { ReactComponent as Instagram } from "../../../../assets/icons/socialIcons/instagramicon.svg";
import { ReactComponent as Facebook } from "../../../../assets/icons/socialIcons/FACEBOOK.svg";
import { ReactComponent as Globe } from "../../../../assets/icons/socialIcons/GlobeIcon.svg";
import { profileStatus, VERIFIED } from "./profileStatus";

/* The masthead's controls: the company's own links, the More menu, and the verification pill. */
/* Transcribed verbatim from BannerCompanyCard's local `checkURL` — the stored value is often bare ("trevex.io"), which without a scheme would open as a path... */
export const checkURL = (url) => {
  if (!/^https?:\/\//i.test(url)) return `http://${url}`;
  return url;
};

export const SOCIAL_LINKS = [
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin },
  { key: "twitter", label: "X", Icon: Twitter },
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "website", label: "Website", Icon: Globe },
];

const BOX = cn(
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border",
  "border-[var(--line)] bg-white text-[var(--ink-2)] transition-colors",
  "hover:border-[var(--line-2)] hover:text-[var(--ink)]",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]",
  "[&_svg]:h-4 [&_svg]:w-4"
);

export const ProfileSocials = ({ company, onShare, className = "" }) => {
  const links = SOCIAL_LINKS.filter((l) => company?.[l.key]);
  if (links.length === 0 && !onShare) return null;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {links.map(({ key, label, Icon }) => (
        <a
          key={key}
          href={checkURL(company[key])}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${label} — opens in a new tab`}
          className={BOX}
        >
          <Icon aria-hidden="true" />
        </a>
      ))}

      {onShare && (
        <button type="button" onClick={onShare} aria-label="Share this profile" className={BOX}>
          <UploadIcon size={16} />
        </button>
      )}
    </div>
  );
};

/* The More menu. The two options and their disabled rules are TRANSCRIBED from BannerCompanyCard: `moreOptions` plus `isOptionDisabled` — Save Business as PDF... */
export const moreOptionDisabled = (option, { id, role, purchaseSubScriptionValid }) => {
  if (option === "Save Business as PDF") {
    return !String(id || "").startsWith("TRX") || (!purchaseSubScriptionValid && !role);
  }
  if (option === "Manage Roles") {
    return role === "ED" || Boolean(purchaseSubScriptionValid);
  }
  return false;
};

/* Transcribed: the live menu holds exactly these two (BannerCompanyCard:131, with "Email Preferences" commented out). */
export const MORE_OPTIONS = [
  { name: "Save Business as PDF", Icon: DocumentIcon },
  { name: "Manage Roles", Icon: UsersIcon },
];

/* Whether the More control is drawn at all. */
export const moreMenuHidden = ({ userInfo, role, purchaseSubScriptionValid, id }) =>
  !userInfo ||
  (!role && !purchaseSubScriptionValid) ||
  !String(id || "").startsWith("TRX");

/* Transcribed `areBothOptionsDisabled`: the control is greyed — not hidden —
   when neither of its two options can be used. */
export const moreMenuDisabled = (ctx) =>
  MORE_OPTIONS.every((o) => moreOptionDisabled(o.name, ctx));

export const ProfileMoreMenu = ({
  id,
  role,
  purchaseSubScriptionValid,
  /* The session user. The live control is hidden outright for a guest. */
  userInfo,
  onSelect = () => {},
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  /* A menu that outlives a click elsewhere is a menu that gets in the way. */
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const ctx = { id, role, purchaseSubScriptionValid };
  const options = MORE_OPTIONS.map((o) => ({
    ...o,
    disabled: moreOptionDisabled(o.name, ctx),
  }));

  /* Transcribed: hidden for a guest, for a viewer with neither a role nor a
     live purchase, and on a non-TREVEX record. */
  if (moreMenuHidden({ userInfo, role, purchaseSubScriptionValid, id })) return null;

  /* And greyed, not hidden, when both of its options are unusable. */
  const allOff = moreMenuDisabled(ctx);

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button
        type="button"
        disabled={allOff}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          "inline-flex h-11 items-center gap-2 rounded-lg border px-4",
          "text-[14px] font-semibold font-sans transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--coral)]",
          allOff
            ? "cursor-not-allowed border-[var(--line-2)] bg-[var(--slate-2)] text-[var(--muted-2)] opacity-60"
            : "border-[var(--coral)] bg-white text-[var(--coral)] hover:bg-[var(--coral-soft)]"
        )}
      >
        More
        <ChevronDown open={open} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="More profile actions"
          className={cn(
            "absolute left-0 top-full z-50 mt-2 w-[248px] overflow-hidden rounded-xl",
            "border border-[var(--line)] bg-white py-1.5 shadow-[shadow:var(--menu-shadow)]"
          )}
        >
          {options.map(({ name, Icon, disabled }) => (
            <button
              key={name}
              type="button"
              role="menuitem"
              disabled={disabled}
              onClick={() => {
                setOpen(false);
                onSelect(name);
              }}
              className={cn(
                "flex w-full items-center gap-3 px-4 py-2.5 text-left",
                "text-[14px] text-[var(--ink)] font-sans transition-colors",
                "hover:bg-[var(--bg)]",
                "disabled:cursor-not-allowed disabled:text-[var(--muted-2)] disabled:hover:bg-transparent"
              )}
            >
              <span className="shrink-0 text-[var(--muted)]">
                <Icon size={16} />
              </span>
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* The verification pill. Drawn only for a verified company — it is an
   assurance, not a label. */
export const VerifiedByTrevex = ({ company, className = "" }) => {
  if (profileStatus(company) !== VERIFIED) return null;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-3.5 py-2",
        "border-[var(--verified-border)] bg-[var(--success-soft)]",
        "text-[13px] font-semibold text-[var(--success)] font-sans",
        className
      )}
    >
      <StatusGlyph kind="verified" size={14} />
      Verified by TREVEX
    </span>
  );
};

export default ProfileMoreMenu;
